import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORAGE_KEY = "umasaura:route-scroll:v1";
type ScrollEntry = {
  y: number;
  anchor?: { productId: string; offset: number; sectionId?: string };
};

export function useRouteScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map<string, ScrollEntry>());
  const restoringKey = useRef<string | null>(null);

  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    try {
      const entries = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
      for (const [key, value] of entries) {
        // Accept positions saved by earlier versions of the storefront.
        const entry = typeof value === "number" ? { y: value } : value;
        if (typeof key === "string" && Number.isFinite(entry?.y) && entry.y >= 0) {
          positions.current.set(key, entry);
        }
      }
    } catch {
      positions.current.clear();
    }
    return () => { window.history.scrollRestoration = previous; };
  }, []);

  useLayoutEffect(() => {
    const key = location.key;
    const saved = positions.current.get(key);
    let frame = 0;
    let timeout = 0;
    let settleTimer = 0;
    let observer: ResizeObserver | undefined;
    let cancelled = false;

    const cleanup = () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
      clearTimeout(settleTimer);
      observer?.disconnect();
      if (restoringKey.current === key) restoringKey.current = null;
      window.removeEventListener("wheel", cleanup);
      window.removeEventListener("touchmove", cleanup);
      window.removeEventListener("pointerdown", cleanup);
      window.removeEventListener("keydown", cleanup);
    };

    if (navigationType === "POP" && saved) {
      restoringKey.current = key;
      const restore = (force = false) => {
        if (cancelled) return;
        const scope = saved.anchor?.sectionId
          ? document.getElementById(saved.anchor.sectionId)
          : document;
        const cards = scope?.querySelectorAll<HTMLElement>("[data-product-id]");
        const card = saved.anchor && cards
          ? Array.from(cards).find(item => item.dataset.productId === saved.anchor!.productId)
          : undefined;
        // A lazy route or its catalog may still be loading. Keep the saved
        // position intact until its product cards exist.
        if (!force && saved.anchor && (!scope || !cards?.length)) return;
        const y = card && saved.anchor
          ? window.scrollY + card.getBoundingClientRect().top - saved.anchor.offset
          : saved.y;
        const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        if (!force && !card && max < y) return;
        window.scrollTo({ top: Math.max(0, Math.min(y, max)), left: 0, behavior: "instant" });
        clearTimeout(settleTimer);
        // Recently viewed products and banners can add height above the card
        // after the first commit. Follow those layout changes briefly.
        settleTimer = window.setTimeout(cleanup, 1000);
      };
      observer = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => restore());
      });
      observer.observe(document.documentElement);
      observer.observe(document.body);
      timeout = window.setTimeout(() => { restore(true); cleanup(); }, 10_000);
      for (const event of ["wheel", "touchmove", "pointerdown", "keydown"] as const) {
        window.addEventListener(event, cleanup, { passive: true });
      }
      restore();
    } else if (location.hash) {
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          let target: HTMLElement | null = null;
          try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch { /* Invalid fragment. */ }
          if (target) target.scrollIntoView({ block: "start", behavior: "instant" });
          else window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        });
      });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    return cleanup;
  }, [location.hash, location.key, navigationType]);

  useLayoutEffect(() => {
    const key = location.key;
    let timer = 0;
    const persist = () => {
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...positions.current])); } catch { /* Storage is optional. */ }
    };
    const save = (event: Event) => {
      // A Link updates history before React removes the outgoing page. Ignore
      // scroll events from its removal or from the destination's scroll reset.
      if ((window.history.state?.key ?? "default") !== key || restoringKey.current === key) return;
      const entry: ScrollEntry = { y: window.scrollY };
      const link = event.type === "click" && event.target instanceof Element
        ? event.target.closest<HTMLAnchorElement>("a[href]")
        : null;
      const card = link?.getAttribute("href")?.startsWith("/product/")
        ? link.closest<HTMLElement>("[data-product-id]")
        : null;
      if (card?.dataset.productId) {
        entry.anchor = {
          productId: card.dataset.productId,
          offset: card.getBoundingClientRect().top,
          sectionId: card.closest("section[id]")?.id,
        };
      }
      positions.current.set(key, entry);
      if (positions.current.size > 100) positions.current.delete(positions.current.keys().next().value!);
      clearTimeout(timer);
      if (event.type === "pagehide") persist();
      else timer = window.setTimeout(persist, 200);
    };
    window.addEventListener("scroll", save, { passive: true });
    document.addEventListener("click", save, true);
    window.addEventListener("pagehide", save);
    return () => {
      // Never sample scrollY during cleanup: the next route may already have
      // changed the document height or scrolled the window to zero.
      window.removeEventListener("scroll", save);
      document.removeEventListener("click", save, true);
      window.removeEventListener("pagehide", save);
      clearTimeout(timer);
      persist();
    };
  }, [location.key]);
}
